import React, { useState } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  IconButton,
  Typography,
} from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { BackdropProgress } from '@shared/components/backdrop-progress.component'
import { originalIcons } from '@shared/components/icons/icons'
import { useScopesQuery } from '@shared/hooks/use-scopes-query.hook'
import { ProgressButton } from '@shared/components/progress-button.component'
import { RequirementMappingCompaniesTree } from './requirement-mapping-companies-tree'
import { api } from '@shared/utils/api/api.util'
import { Location, Scope } from '@shared/types'
import RequirementsTreeAdmin from './requirements-mapping-tree-admin.component'

const AccordionIcon = ({ expanded }: { expanded: boolean }) => {
  return expanded ? <originalIcons.Indeterminate /> : <originalIcons.Add />
}

export const RequirementMappingTree = ({
  companiesIds,
  onCompaniesMappingChange,
  currentScope,
  isResourceEdit,
  isPublished,
}: {
  companiesIds: number[]
  onCompaniesMappingChange: (companiesIDs: number[]) => void
  currentScope: Scope | null
  isResourceEdit: boolean
  isPublished: boolean
}) => {
  const classes = useStyles()
  const [openSection, setOpenSection] = useState(false)
  const [isLoadingFetchingCompanies, setIsLoadingFetchingCompanies] =
    useState(false)
  const [openRequirements, setOpenRequirements] = useState(true)
  const [openCompanies, setOpenCompanies] = useState(!isResourceEdit)
  const [selectedRequirements, setSelectedRequirements] = useState<number[]>([])
  const { areScopesLoading, scopes } = useScopesQuery()

  // const handleSelectDeselectAll = () => {
  //   if (!companiesData) return

  //   if (allSelected) {
  //     onCompaniesMappingChange([]) // Deselect all
  //   } else {
  //     const allIds = companiesData.map((company) => company.companyID)
  //     onCompaniesMappingChange(allIds) // Select all
  //   }

  //   setAllSelected(!allSelected)
  // }

  const handleToggle = (_: any, expanded: boolean) => {
    setOpenSection(expanded)
  }

  const handleToggleRequirements = (_: any, expanded: boolean) => {
    setOpenRequirements(expanded)
  }

  const handleToggleCompanies = (_: any, expanded: boolean) => {
    setOpenCompanies(expanded)
  }

  const getCompaniesByRequirements = async () => {
    try {
      setIsLoadingFetchingCompanies(true)
      const ids = selectedRequirements.join(',')
      const response = await api(`companies/requirements?ids=${ids}`)
      if (response.ok) {
        setOpenRequirements(false)
        setOpenCompanies(true)
        const result = (await response.json()) as Location[]
        onCompaniesMappingChange(result.map((company) => company.companyID))
      }
    } catch (error) {
      console.error('Error fetching companies by requirements:', error)
    } finally {
      setIsLoadingFetchingCompanies(false)
    }
  }

  if (isPublished) {
    return areScopesLoading ? (
      <BackdropProgress />
    ) : (
      <Accordion
        component="div"
        variant="outlined"
        className={classes.wrapper}
        expanded={openCompanies}
        onChange={handleToggleCompanies}
      >
        <AccordionSummary>
          <Typography color="textSecondary">Lokalizacje</Typography>
          <IconButton
            color="primary"
            component="span"
            className={classes.addIcon}
            size="large"
          >
            <AccordionIcon expanded={openCompanies} />
          </IconButton>
        </AccordionSummary>
        <AccordionDetails sx={{ transitionDuration: '0.01s !important' }}>
          <RequirementMappingCompaniesTree
            fetchEnabled={openCompanies}
            setSelectedIds={onCompaniesMappingChange}
            selectedIds={companiesIds}
            isResourceEdit={isResourceEdit}
            isPublished={isPublished}
          />
        </AccordionDetails>
      </Accordion>
    )
  }

  return !areScopesLoading ? (
    <Accordion
      component="div"
      variant="outlined"
      className={classes.wrapper}
      expanded={openSection}
      onChange={handleToggle}
    >
      <AccordionSummary>
        <Typography color="textSecondary">Mapowanie wymagań</Typography>
        <IconButton
          color="primary"
          component="span"
          className={classes.addIcon}
          onClick={(e) => {
            e.stopPropagation()
            handleToggle(e, !openSection)
          }}
          size="large"
          disabled={!currentScope}
        >
          <AccordionIcon expanded={openSection} />
        </IconButton>
      </AccordionSummary>
      <AccordionDetails>
        <Accordion
          expanded={openRequirements}
          onChange={handleToggleRequirements}
        >
          <AccordionSummary>
            <Typography color="textSecondary">
              Skopiuj z innych wymagań
            </Typography>
            <IconButton
              color="primary"
              component="span"
              className={classes.addIcon}
              size="large"
            >
              <AccordionIcon expanded={openRequirements} />
            </IconButton>
          </AccordionSummary>
          <AccordionDetails>
            {scopes ? (
              <RequirementsTreeAdmin
                type="requirements"
                header={<></>}
                scopes={scopes}
                selected={selectedRequirements}
                setSelected={setSelectedRequirements}
                currentScope={currentScope}
              />
            ) : null}
            <ProgressButton
              color="primary"
              variant="contained"
              isLoading={isLoadingFetchingCompanies}
              onClick={getCompaniesByRequirements}
              sx={{ marginTop: 2 }}
            >
              Zaznacz lokalizacje zawierające powyższe wymagania
            </ProgressButton>
          </AccordionDetails>
        </Accordion>
        <Accordion
          expanded={openCompanies}
          onChange={handleToggleCompanies}
          sx={{ marginTop: 2 }}
        >
          <AccordionSummary>
            <Typography color="textSecondary">Lokalizacje</Typography>
            <IconButton
              color="primary"
              component="span"
              className={classes.addIcon}
              size="large"
            >
              <AccordionIcon expanded={openCompanies} />
            </IconButton>
          </AccordionSummary>
          <AccordionDetails sx={{ transitionDuration: '0.01s !important' }}>
            <RequirementMappingCompaniesTree
              fetchEnabled={openCompanies}
              setSelectedIds={onCompaniesMappingChange}
              selectedIds={companiesIds}
              isResourceEdit={isResourceEdit}
              isPublished={isPublished}
            />
          </AccordionDetails>
        </Accordion>
      </AccordionDetails>
    </Accordion>
  ) : (
    <BackdropProgress />
  )
}

const useStyles = makeStyles(() => {
  return {
    wrapper: {
      marginTop: 15,
      position: 'relative',
    },
    addIcon: {
      position: 'absolute',
      top: 0,
      right: 10,
    },
  }
})
